/*
 * @Author: mayqiyue
 * @Date: 2018-11-16 10:39:32
 * @Last Modified by: mayqiyue
 * @Last Modified time: 2019-03-06 18:24:03
 */

import store from "@/store";
import GeneralUtilsHelper from "@/utils/generalUtils";
import { isIos } from "@/utils/lodash";

const VipOfferUtils: any = {
  /**
   * 当前设置项中第一个 vip offer
   */
  firstVipService: () => {
    let services: any = [];
    if (store.state.settingModule.settingData.hasOwnProperty("service_offers")) {
      services = store.state.settingModule.settingData.service_offers;
    }
    return services.find((item: any) => item.effect === 1);
  },

  /**
   * 返回用户购买过的 vip 结构，如果没有则返回 undefined
   */
  boughtVip: () => {
    const services = store.state.gxUserInfoModule.gxUserInfo.services;
    if (!services) {
      return undefined;
    }
    return services.find((item: any) => item.kind === "vip");
  },

  /**
   * 判断一个 service 是不是自动续订类型
   */
  isAutoRenew: (service: any) => {
    if (!service) {
      return true;
    }
    if (service.hasOwnProperty("is_no_auto_renew")) {
      return service.is_no_auto_renew === false;
    }
    if (service.hasOwnProperty("pay_source") && service.pay_source === "paypal") {
      return true;
    }
    return true;
  },

  expiredDays: (service: any) => {
    if (!service) {
      return 0;
    }
    const expireTime = service.expire; // if null, expire is 0
    const now = Math.max(new Date().valueOf(), GeneralUtilsHelper.getCurrentServeTime());
    let days = Math.floor((now - expireTime) / 86400000);
    if (days === 0) {
      days = 1;
    }
    return days;
  },

  hasExpired: (service: any) => {
    if (!service) {
      return false;
    }
    const expireTime = service.expire; // if null, expire is 0
    const now = Math.max(new Date().valueOf(), GeneralUtilsHelper.getCurrentServeTime());
    return now - expireTime >= 0;
  },

  /**
   * 过期天数超过该值进行相应的renew UI 的显示
   */
  expiredDayThreshold: (service: any) => {
    if (!service) {
      return 0;
    }
    if (VipOfferUtils.isAutoRenew(service)) {
      return 0;
    }

    let effect: number = 1;
    if (service.hasOwnProperty("effect")) {
      effect = service.effect;
    } else {
      // 从 userinfo 中获取的购买过的service没有effect字段
      let services: any = [];
      if (store.state.settingModule.settingData.hasOwnProperty("service_offers")) {
        services = store.state.settingModule.settingData.service_offers;
      }
      const targetService = services.find((item: any) => item.offer_id === service.offer_id);
      if (targetService) {
        effect = targetService.effect;
      }
    }

    if (effect >= 6) {
      return 30;
    }

    return 7;
  },

  /**
   * 判断购买该 service 是否会有金币奖励, 默认为 0
   */
  coinsBonus: (service: any) => (VipOfferUtils.boughtVip() ? VipOfferUtils.renewCoinBonus(service) : VipOfferUtils.dailyCoinsReward(service)),

  renewCoinBonus: (service: any) => {
    if (service === undefined) {
      return 0;
    }
    const coins: number = service.hasOwnProperty("bonus") ? service.bonus.coin : 0;
    return coins;
  },

  /**
   *
   * 判断购买该 service 是否会有额外赠送的月份, 默认为0
   */
  monthsBonus: (service: any) => {
    if (VipOfferUtils.boughtVip()) {
      return 0;
    }
    if (!service) {
      return 0;
    }
    if (!service.hasOwnProperty("first_bonus")) {
      return 0;
    }
    if (!Array.isArray(service.first_bonus)) {
      return 0;
    }

    let periodDays = 0;

    service.first_bonus.forEach((dict: any) => {
      if (dict.hasOwnProperty("bonus") && Array.isArray(dict.bonus)) {
        dict.bonus.forEach((item: any) => {
          if (item.period_day && item.type) {
            if (item.type === "vip") {
              periodDays += item.period_day;
            }
          }
        });
      }
    });

    return Math.floor(periodDays / 30);
  },

  /**
   *  首次购买的金币奖励，由 service_daily_bonus_limit_sale 字段进行时间段的选择
   */
  dailyCoinsReward: (service: any) => {
    if (!service) {
      return 0;
    }
    if (!VipOfferUtils.isDailyBonusVaild()) {
      return 0;
    }
    if (!service.hasOwnProperty("daily_bonus_info")) {
      return 0;
    }

    let coins = 0;

    const keys = service.free_days && service.free_days > 0 ? ["is_trial", "is_not_trial"] : ["is_not_trial"];
    const bonusInfo: any = service.daily_bonus_info;
    keys.forEach((key: string) => {
      if (bonusInfo.hasOwnProperty(key) && Array.isArray(bonusInfo[key])) {
        bonusInfo[key].forEach((dict: any) => {
          if (dict.hasOwnProperty("bonus") && Array.isArray(dict.bonus)) {
            dict.bonus.forEach((item: any) => {
              if (item.type && item.effect) {
                if (item.type === "coin") {
                  coins += item.effect;
                }
              }
            });
          }
        });
      }
    });
    return coins;
  },

  isDailyBonusVaild: () => {
    const limitSale = VipOfferUtils.serviceDailyBonusLimitSale();
    if (!limitSale) {
      return false;
    }
    const now = new Date().valueOf();
    if (limitSale.start_time <= now && now <= limitSale.end_time) {
      return true;
    }
    return false;
  },

  serviceDailyBonusLimitSale: () => {
    const limitSale = store.state.settingModule.settingData && store.state.settingModule.settingData.service_daily_bonus_limit_sale;
    if (limitSale && limitSale.hasOwnProperty("start_time") && limitSale.hasOwnProperty("end_time")) {
      return limitSale;
    }
    return undefined;
  },

  shouldShowCoinBonus: () => {
    return (
      store.state.settingModule.settingData &&
      store.state.settingModule.settingData.enable &&
      store.state.settingModule.settingData.enable.show_vip_bonus_coins_enabled &&
      store.state.settingModule.settingData.enable.show_vip_bonus_coins_enabled === true
    );
  },

  /**
   * 根据服务器返回，的判断某个 VIP 订单中有多少送的金币到货
   */
  deliveredBonusCoins: (order: any) => {
    if (!order) {
      return 0;
    }
    if (order.bonus && order.bonus.coin && order.bonus.deliver_timestamp) {
      return order.bonus.coin;
    }
    if (!order.daily_bonus_info) {
      return 0;
    }
    let coins = 0;

    const keys = order.is_trial && order.is_trial === true ? ["is_trial", "is_not_trial"] : ["is_not_trial"];
    const bonusInfo: any = order.daily_bonus_info;
    keys.forEach((key: string) => {
      if (bonusInfo[key] && Array.isArray(bonusInfo[key])) {
        bonusInfo[key].forEach((dict: any) => {
          if (dict.bonus && Array.isArray(dict.bonus) && dict.deliver_timestamp) {
            dict.bonus.forEach((item: any) => {
              if (item.type && item.effect) {
                if (item.type === "coin") {
                  coins += item.effect;
                }
              }
            });
          }
        });
      }
    });
    return coins;
  },
  vipEntranceListFirstContent: () => {
    const firstService = VipOfferUtils.firstVipService();
    const service: any = VipOfferUtils.boughtVip();
    const coins: number = VipOfferUtils.shouldShowCoinBonus() ? VipOfferUtils.coinsBonus(firstService) : 0;

    const normalContents = {
      icon: require("@/assets/icons-file/more/become_vip@3x.png"),
      title: "Become VIP",
      content: `Upgrade for the exclusive privileges and all <span class="block">following full features 👉</span>`
    };

    if (service) {
      const expiredDay: number = VipOfferUtils.expiredDays(service);
      const threshold: number = VipOfferUtils.expiredDayThreshold(service);

      let exprString = "";
      [...Math.abs(expiredDay).toString()].forEach(c => {
        exprString += `<h5>${c}</h5>`;
      });

      if (expiredDay > threshold) {
        return normalContents;
      } else if (expiredDay > 0) {
        return coins > 0
          ? {
              icon: require("@/assets/icons-file/more/become_vip@3x.png"),
              title: "RENEW VIP",
              content: `Your VIP expired ${exprString} days ago. Join now to get <span>${coins.toString()}</span> bonus coins for <span>FREE!</span>`
            }
          : {
              icon: require("@/assets/icons-file/more/become_vip@3x.png"),
              title: "RENEW VIP",
              content: `Your VIP expired ${exprString} days ago.`
            };
      } else if (expiredDay > -threshold) {
        return coins > 0
          ? {
              icon: require("@/assets/icons-file/more/become_vip@3x.png"),
              title: "RENEW VIP",
              content: `Your VIP will expire in ${exprString} days. Join now to get <span>${coins.toString()}</span> bonus coins for <span>FREE!</span>`
            }
          : {
              icon: require("@/assets/icons-file/more/become_vip@3x.png"),
              title: "RENEW VIP",
              content: `Your VIP will expire in ${exprString} days.`
            };
      }
    }

    return normalContents;
  },

  vipButtonContents: (service: any, isForDilog: boolean) => {
    const boughtVip: any = VipOfferUtils.boughtVip();
    const coins: number = VipOfferUtils.shouldShowCoinBonus() ? VipOfferUtils.coinsBonus(service) : 0;
    const months: number = VipOfferUtils.monthsBonus(service);
    const hasFreeTry: boolean = service && service.hasOwnProperty("free_days") && service.free_days > 0;
    const isExpired = VipOfferUtils.hasExpired(boughtVip);

    if (boughtVip) {
      if (isExpired) {
        if (!VipOfferUtils.isAutoRenew(boughtVip) && VipOfferUtils.isAutoRenew(service)) {
          return coins > 0 ? { main: "FREE", sub: "TRY FOR", bonus: "GET " + coins.toString(), bonusType: "coin" } : { main: "TRY FOR FREE" };
        }
      }
      if (isForDilog) {
        return { main: "PAY NOW" };
      }
      return coins > 0 ? { main: "FREE", sub: "RENEW NOW", bonus: "GET " + coins.toString(), bonusType: "coin" } : { main: "RENEW NOW" };
    } else {
      if (months === 0 && coins === 0 && !hasFreeTry) {
        // Not fetch the settings the first time
        if (service.hasOwnProperty("effect") && service.effect > 1) {
          return { main: "CONTINUE" };
        } else {
          return { main: "FREE", sub: "BUY 1 mo", bonus: "GET 1 mo", bonusType: "mo" };
        }
      } else if (months > 0 && coins > 0) {
        return { main: "FREE", sub: "BUY " + service.effect.toString() + " GET " + months.toString(), bonus: "GET " + coins.toString(), bonusType: "coin" };
      } else if (months > 0) {
        return { main: "FREE", sub: "BUY " + service.effect.toString() + " mo", bonus: "GET " + months.toString() + " mo", bonusType: "mo" };
      } else if (coins > 0) {
        return { main: "FREE", sub: hasFreeTry ? "TRY FOR" : "CONTINUE", bonus: "GET " + coins.toString(), bonusType: "coin" };
      } else {
        return { main: hasFreeTry ? "TRY FOR FREE" : "CONTINUE" };
      }
    }
  },

  vipLeaveDialogContents: (service: any) => {
    const boughtVip: any = VipOfferUtils.boughtVip();
    const coins: number = VipOfferUtils.shouldShowCoinBonus() ? VipOfferUtils.coinsBonus(service) : 0;
    const months: number = VipOfferUtils.monthsBonus(service);
    const hasFreeTry: boolean = service && service.hasOwnProperty("free_days") && service.free_days > 0;
    let title: any;
    let text: any;
    const coinsString = `<span style=" font-size: 16px; color: #ff7e00;"> ${GeneralUtilsHelper.formatToThousands(coins)} </span>`;
    if (boughtVip) {
      title = `Not Ready To Pay?`;
      text = `That's totally OK. We'll give you extra FREE Gifts when you enjoy the exclusive and beneficial VIP service.`;
    } else {
      if (months > 0 && coins > 0) {
        title = `Special VIP Bonus Offer`;
        text = `It's buy ${service.effect.toString()} get ${months.toString()} free time and extra ${coinsString} bonus coins for FREE within a limited time only.`;
      } else if (months > 0) {
        title = `Last Offer Reward`;
        text = `Get your VIP membership NOW! Buy ${service.effect.toString()}, Get ${months.toString()} Free deal is LIVE!`;
      } else if (coins > 0) {
        title = `Special VIP Bonus Offer`;
        text = `Join Now to get a FREE TRIAL offer and extra ${coinsString} bonus coins for FREE within a limited time only.`;
      } else {
        title = `Last Offer Reward`;
        text = `Don’t forget that we have a FREE TRIAL that you can test right now!`;
      }
    }
    return { title, text };
  },

  isShowBounsCoinsCarousel: () => {
    const isBoughtVip = VipOfferUtils.boughtVip();
    const firstService = VipOfferUtils.firstVipService();
    const renewCoinBonus = VipOfferUtils.renewCoinBonus(firstService);
    const isDailyBonusVaild = VipOfferUtils.isDailyBonusVaild();
    return (isBoughtVip && renewCoinBonus) || (!isBoughtVip && isDailyBonusVaild) ? true : false;
  },

  vipPriceText: () => (isIos() ? { dayMoney: "4.99", monthMoney: "119.99" } : { dayMoney: "3.99", monthMoney: "49.99" }),
  vipSaveMoney: () => (isIos() ? "59.99" : "43.99")
};

export default VipOfferUtils;
